import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Table } from "../CommonUIComponents/Table/Table";
import { SearchInput } from "../CommonUIComponents/SearchInput/SearchInput";
import { Button } from "../CommonUIComponents/Button/Button";
import { ToastMessage } from "../CommonUIComponents/ToastMessage/ToastMessage";
import { AddUserModal } from "../AddUserModal/AddUserModal";
import { ConfirmationModal } from "../CommonUIComponents/ConfirmationModal/ConfimationModal";
import { AddUserIcon } from "../../icons/addUser";
import {
  fetchUserManagementTable,
  updateUserThunk,
  deleteUserThunk,
  fetchRolesThunk,
  fetchSuperRegionsThunk,
  resetSaveError,
  resetDeleteError,
} from "../../store/slices/userManagementSlice";
import { getUserColumnDefs } from "./UserColumns";
import { routes } from "../../routes/routes.constatns";
import "./UserManagement.scss";
import { useI18n } from "../../hooks/useI18n";
import { hasUserManagementAccess } from "../../util/roleHelpers";

// Pulls the most user-friendly message out of various backend error shapes
// (string, { message }, { title, detail }, ASP.NET ModelState { errors: { Field: [..] } }, etc.)
function extractBackendErrorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;

  if (payload.message) return payload.message;

  if (payload.errors && typeof payload.errors === "object") {
    const collected = [];
    for (const key of Object.keys(payload.errors)) {
      const val = payload.errors[key];
      if (Array.isArray(val)) collected.push(...val);
      else if (typeof val === "string") collected.push(val);
    }
    if (collected.length > 0) return collected.join(" ");
  }

  return payload.detail || payload.title || payload.error || fallback;
}

const UserManagement = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const { rows, loading, saveLoading, saveError, deleteLoading } = useSelector(
    (state) => state.userManagement,
  );
  const roleData = useSelector((state) => state.landing.roleData);
  const currentUser = useSelector((state) => state.user.user);

  const canAccessUserManagement = hasUserManagementAccess(roleData?.role);
  const currentUserEmail = currentUser?.username || currentUser?.email || "";

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [isViewMode, setIsViewMode] = useState(false);

  const regionString = "";

  useEffect(() => {
    dispatch(fetchRolesThunk());
    dispatch(fetchSuperRegionsThunk());
  }, [dispatch]);

  const handleSearchChange = useCallback((event) => {
    const value = event?.target ? event.target.value : event;
    setSearchText(value || "");
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchText("");
    setDebouncedSearchText("");
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const refreshTable = useCallback(() => {
    dispatch(
      fetchUserManagementTable({
        pageNumber: 1,
        search: debouncedSearchText || null,
        coe: "",
        region: regionString,
      }),
    );
  }, [dispatch, debouncedSearchText, regionString]);

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsViewMode(false);
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setSelectedUser(null);
    setIsViewMode(false);
    dispatch(resetSaveError());
  };

  const handleAssignUser = async (payload) => {
    const isEdit = Boolean(selectedUser);
    const addedBy = currentUser?.username || currentUser?.email || "";

    const userData = {
      userManagementID: isEdit ? Number(selectedUser.id) : 0,
      userEmail: payload.email,
      firstName:
        payload.firstName || (isEdit ? selectedUser.firstName || "" : ""),
      lastName: payload.lastName || (isEdit ? selectedUser.lastName || "" : ""),
      role: payload.role,
      roleId: payload.roleId,
      coe: payload.coe,
      isActive: isEdit ? selectedUser.isActiveRaw || "Yes" : "Yes",
      region: regionString,
      addedby: addedBy,
      superRegionIDs: payload.superRegionIDs || [],
      regionIDs: payload.regionIDs || [],
      countryIds: payload.countryIds || [],
      processingTeamType: payload.processingTeamType,
      processingTeamCountryIds: payload.processingTeamCountryIds || [],
    };

    const result = await dispatch(updateUserThunk(userData));

    if (!result.error) {
      setShowAddUserModal(false);
      setSelectedUser(null);
      setToastMessage(
        isEdit ? "User updated successfully" : "User added successfully",
      );
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      refreshTable();
    }
  };

  const handleEditUser = useCallback(
    (userId) => {
      const userToEdit = rows.find((user) => user.id === userId) || null;
      setSelectedUser(userToEdit);
      setIsViewMode(false);
      setShowAddUserModal(true);
    },
    [rows],
  );


  const handleDeleteUser = useCallback(
    (userId) => {
      const userRow = rows.find((user) => user.id === userId) || null;
      if (!userRow) return;
      setUserToDelete(userRow);
      setDeleteErrorMessage("");
      setShowDeleteModal(true);
    },
    [rows],
  );

  const handleCancelDelete = useCallback(() => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteErrorMessage("");
    dispatch(resetDeleteError());
  }, [dispatch, deleteLoading]);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;

    setDeleteErrorMessage("");

    const result = await dispatch(
      deleteUserThunk({ userManagementID: Number(userToDelete.id) }),
    );

    if (!result.error) {
      setShowDeleteModal(false);
      setUserToDelete(null);
      setToastVariant("success");
      setToastMessage(t("user_mng_deleteSuccess"));
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      refreshTable();
    } else {
      setDeleteErrorMessage(
        extractBackendErrorMessage(result.payload, t("user_mng_deleteError")),
      );
    }
  }, [dispatch, userToDelete, refreshTable, t]);

  const handleViewUser = useCallback((user) => {
  setSelectedUser(user);
  setIsViewMode(true);
  setShowAddUserModal(true);
}, []);


const columnDefs = useMemo(
  () =>
    getUserColumnDefs(
      handleEditUser,
      handleDeleteUser,
      handleViewUser,
      currentUserEmail,
      t
    ),
  [
    handleEditUser,
    handleDeleteUser,
    handleViewUser,
    currentUserEmail,
    t,
  ],
);

  const rowClassRules = {
    "user-row-even": (params) => params.node.rowIndex % 2 === 0,
    "user-row-odd": (params) => params.node.rowIndex % 2 !== 0,
  };

  const saveErrorMessage = useMemo(
    () =>
      saveError
        ? extractBackendErrorMessage(saveError, "Failed to save user")
        : "",
    [saveError],
  );

  if (!canAccessUserManagement) {
    return <Navigate to={`/${routes.home}`} replace />;
  }

  return (
    <div className="user-management-container">
      <h1 className="user-management-title">{t("user_mng_header")}</h1>

      <div className="user-management-panel">
        <div className="user-management-actions">
          <SearchInput
            placeholder={t("search")}
            value={searchText}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            className="user-management-search"
          />
          <Button
            variant="secondary"
            onClick={handleAddUser}
            className="user-management-add-button"
            size="medium"
          >
            <AddUserIcon />
            <span>{t("user_mng_addUser")}</span>
          </Button>
        </div>

        <div className="user-management-table">
          <Table
            rowData={rows}
            colDefs={columnDefs}
            pagesize={10}
            pagination={true}
            loading={loading}
            suppressMovableColumns={false}
            rowClassRules={rowClassRules}
            rowHeight={44}
            headerHeight={44}
          />
        </div>
      </div>

      <AddUserModal
        show={showAddUserModal}
        onClose={handleCloseAddUserModal}
        onAssign={handleAssignUser}
        initialValues={selectedUser}
        loading={saveLoading}
        error={saveErrorMessage}
        onClearError={() => dispatch(resetSaveError())}
        isViewMode={isViewMode}
        currentUserEmail={currentUserEmail}
      />

      <ConfirmationModal
        isModalVisible={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        className="user-mgmt-delete-modal"
        confirmVariant="secondary"
        confirmClassName="user-mgmt-delete-modal__confirm"
        isConfirming={deleteLoading}
        cancelButtonText={t("cancel")}
        confirmButtonText={
          deleteLoading ? t("user_mng_deleting") : t("user_mng_deleteButton")
        }
        modalHeaderText={
          <span className="user-mgmt-delete-modal__title">
            <span
              className="user-mgmt-delete-modal__title-icon"
              aria-hidden="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span>{t("user_mng_deleteConfirmTitle")}</span>
          </span>
        }
        modalBodyText={
          <div className="user-mgmt-delete-modal__body">
            <p className="user-mgmt-delete-modal__question">
              {t("user_mng_deleteConfirmBody")}
            </p>
            {userToDelete && (
              <div
                className="user-mgmt-delete-modal__user-card"
                aria-label={t("user_mng_userEmail")}
              >
                <span className="user-mgmt-delete-modal__user-label">
                  {t("user_mng_userEmail")}
                </span>
                <span className="user-mgmt-delete-modal__user-email">
                  {userToDelete.email}
                </span>
                {userToDelete.role && (
                  <span className="user-mgmt-delete-modal__user-role">
                    {Array.isArray(userToDelete.role)
                      ? userToDelete.role.join(", ")
                      : userToDelete.role}
                  </span>
                )}
              </div>
            )}
            {(() => {
                const pt = userToDelete?.processingTeamType;
                const isGds = Array.isArray(pt)
                  ? pt.includes("GDS")
                  : pt === "GDS";
                const role = Array.isArray(userToDelete?.role)
                  ? userToDelete.role.join(", ")
                  : userToDelete?.role || "";
                const isPmo = role === "Resource Manager/PMO";
                return isGds && isPmo ? (
                  <div
                    className="user-mgmt-delete-modal__warning-note"
                    role="note"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      marginTop: "12px",
                      padding: "10px 12px",
                      background: "#2e2e38",
                      borderLeft: "3px solid #ffe600",
                      borderRadius: "4px",
                      fontSize: "13px",
                      color: "#c4c4cd",
                    }}
                  >
                    <span aria-hidden="true" style={{ flexShrink: 0, marginTop: "1px" }}>⚠️</span>
                    <span>
                      Deleting a <strong>Resource Manager/PMO</strong> role will also delete associated reporting roles internally.
                    </span>
                  </div>
                ) : null;
              })()}
            {deleteErrorMessage && (
              <div
                className="user-mgmt-delete-modal__error"
                role="alert"
                aria-live="assertive"
              >
                {deleteErrorMessage}
              </div>
            )}
          </div>
        }
      />

      <ConfirmationModal
        isModalVisible={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        className="user-mgmt-delete-modal"
        confirmVariant="secondary"
        confirmClassName="user-mgmt-delete-modal__confirm"
        isConfirming={deleteLoading}
        cancelButtonText={t("cancel")}
        confirmButtonText={
          deleteLoading ? t("user_mng_deleting") : t("user_mng_deleteButton")
        }
        modalHeaderText={
          <span className="user-mgmt-delete-modal__title">
            <span
              className="user-mgmt-delete-modal__title-icon"
              aria-hidden="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span>{t("user_mng_deleteConfirmTitle")}</span>
          </span>
        }
        modalBodyText={
          <div className="user-mgmt-delete-modal__body">
            <p className="user-mgmt-delete-modal__question">
              {t("user_mng_deleteConfirmBody")}
            </p>
            {userToDelete && (
              <div
                className="user-mgmt-delete-modal__user-card"
                aria-label={t("user_mng_userEmail")}
              >
                <span className="user-mgmt-delete-modal__user-label">
                  {t("user_mng_userEmail")}
                </span>
                <span className="user-mgmt-delete-modal__user-email">
                  {userToDelete.email}
                </span>
                {userToDelete.role && (
                  <span className="user-mgmt-delete-modal__user-role">
                    {Array.isArray(userToDelete.role)
                      ? userToDelete.role.join(", ")
                      : userToDelete.role}
                  </span>
                )}
              </div>
            )}
            {deleteErrorMessage && (
              <div
                className="user-mgmt-delete-modal__error"
                role="alert"
                aria-live="assertive"
              >
                {deleteErrorMessage}
              </div>
            )}
          </div>
        }
      />

      {showToast && (
        <ToastMessage
          variant={toastVariant}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default UserManagement;
